'use client';

import { useState } from 'react';
import { useSearchParams, usePathname } from 'next/navigation';
import { TriangleAlert as AlertTriangle, RefreshCw, LogOut } from 'lucide-react';
import { AppSidebar } from './app-sidebar';
import { AppHeader } from './app-header';
import { Button } from '@/platform/ui/button';
import { Badge } from '@/platform/ui/badge';
import { useUserSessionData, useUserSessionActions } from '@/platform/auth/user-session-context';
import { useProductBranding } from '@/platform/hooks/use-product-branding';
import { signOut } from '@/platform/auth/auth-client';
import { useRouter } from 'next/navigation';

interface AppShellProps {
  children: React.ReactNode;
}

function ParticipantSurveyShell({ children }: { children: React.ReactNode }) {
  const searchParams = useSearchParams();
  const surveyName = searchParams.get('survey_name');
  const router = useRouter();
  const { branding } = useProductBranding();

  const handleSignOut = async () => {
    await signOut();
    router.push('/auth/login');
    router.refresh();
  };

  return (
    <div className="min-h-screen bg-transparent flex flex-col relative z-10">
      <header className="h-14 border-b bg-card flex items-center justify-between px-6 shrink-0">
        <div className="flex items-center gap-3">
          {branding.containerIconUrl ? (
            <img src={branding.containerIconUrl} alt={branding.containerName} className="h-7 w-7 rounded object-contain shrink-0" />
          ) : (
            <div className="h-7 w-7 rounded bg-primary flex items-center justify-center shrink-0">
              <span className="text-primary-foreground text-xs font-bold">{branding.containerName.charAt(0)}</span>
            </div>
          )}
          <span className="font-semibold text-sm hidden sm:block">{branding.containerName}</span>
          {surveyName && (
            <Badge variant="secondary" className="hidden sm:inline-flex text-xs font-normal">
              {surveyName}
            </Badge>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={handleSignOut} className="gap-2 text-muted-foreground">
          <LogOut className="h-4 w-4" />
          <span className="hidden sm:inline">Log out</span>
        </Button>
      </header>
      <main className="flex-1 overflow-y-auto p-6">
        {children}
      </main>
    </div>
  );
}

export function AppShell({ children }: AppShellProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { error, loading, isParticipantOnly } = useUserSessionData();
  const { refresh } = useUserSessionActions();
  const pathname = usePathname();
  const isAssessmentTaking = pathname?.includes('/assessment/');
  const isReportPage = pathname?.endsWith('/report');

  if (isAssessmentTaking || isReportPage) {
    return <>{children}</>;
  }

  if (isParticipantOnly) {
    return <ParticipantSurveyShell>{children}</ParticipantSurveyShell>;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-transparent relative z-10">
      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex flex-col flex-1 overflow-hidden">
        <AppHeader
          onMenuClick={() => setSidebarOpen(!sidebarOpen)}
          sidebarOpen={sidebarOpen}
        />

        {error && !loading && (
          <div className="flex items-center justify-between gap-4 border-b border-destructive/20 bg-destructive/10 px-6 py-3 text-sm text-destructive">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>
                Unable to load your permissions: {error}. Your roles and menu items may be missing until this is resolved.
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={refresh}
              className="shrink-0 border-destructive/40 text-destructive hover:bg-destructive/10"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-2" />
              Retry
            </Button>
          </div>
        )}

        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
}

